import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/SEOHead';

type Tab = 'contact' | 'careers';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d3dhhirbr3iihosg1b90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
      {/* Left info column */}
      <div className="lg:col-span-2 space-y-8">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-5">Contact Information</h3>
          <div className="space-y-5">
            {[
              { icon: 'ri-mail-line', label: 'Email', value: 'info@villarrealgroup.com' },
              { icon: 'ri-map-pin-line', label: 'Location', value: 'Las Vegas, Nevada' },
              { icon: 'ri-time-line', label: 'Business Hours', value: 'Monday – Friday, 9am – 5pm' },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg shrink-0">
                  <i className={`${item.icon} text-lg text-gray-700`}></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                  <p className="text-gray-800 font-medium">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">This form is for:</h4>
          <ul className="space-y-2">
            {[
              'Clients seeking DMV services',
              'Authorized referral sources',
              'Verified business matters',
              'Existing client follow-ups',
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-checkbox-circle-line text-gray-800"></i>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* No Solicitation Warning Box */}
        <div className="border-2 border-red-600 rounded-xl overflow-hidden">
          <div className="bg-red-700 px-4 py-3 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-shield-keyhole-line text-white text-base"></i>
            </div>
            <span className="text-white font-black text-xs tracking-widest uppercase">Strict No Solicitation Policy</span>
          </div>
          <div className="bg-red-50 px-4 py-4 space-y-3 text-xs text-gray-700">
            <p className="leading-relaxed font-medium">
              This website and all associated businesses <strong className="text-red-800">strictly prohibit</strong> any unsolicited communications — including sales, marketing, lead generation, promotional offers, partnerships, or outreach of any kind.
            </p>
            <div className="border-t border-red-200 pt-3 space-y-2">
              <p className="font-bold text-gray-800">By submitting, you agree that:</p>
              <ul className="space-y-1.5">
                {[
                  'Unsolicited sales or marketing messages are strictly prohibited',
                  'Your information may be filtered, blocked, or reported as spam',
                  'Repeated violations may result in regulatory reporting, blacklisting, and legal action',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-close-circle-line text-red-600"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="font-black text-red-700 text-center text-xs pt-1 uppercase tracking-wide">
              We do not engage with cold outreach under any circumstances.
            </p>
            <p className="text-center text-gray-600 text-xs italic">
              Continued disregard constitutes intentional interference with business operations.
            </p>
            <p className="font-bold text-red-800 text-center border-t border-red-200 pt-3">
              Proceed only if your inquiry is legitimate and relevant.
            </p>
          </div>
        </div>
      </div>

      {/* Right form column */}
      <div className="lg:col-span-3">
        <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="contact-page-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
              <input
                type="text" name="name" required value={formData.name} onChange={handleChange}
                placeholder="Your full name"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address *</label>
              <input
                type="email" name="email" required value={formData.email} onChange={handleChange}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company / Organization</label>
            <input
              type="text" name="company" value={formData.company} onChange={handleChange}
              placeholder="Your company name (if applicable)"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Subject *</label>
            <select
              name="subject" required value={formData.subject} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
            >
              <option value="">Select a subject</option>
              <option value="client-service">Client Service Inquiry</option>
              <option value="referral">Authorized Referral</option>
              <option value="existing-client">Existing Client Follow-Up</option>
              <option value="media">Media Inquiry</option>
              <option value="verified-business">Verified Business Matter</option>
              <option value="other">Other Legitimate Inquiry</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message *</label>
            <textarea
              name="message" required rows={6} maxLength={500}
              value={formData.message} onChange={handleChange}
              placeholder="Describe your inquiry clearly and concisely..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
          </div>

          {/* Acknowledgment checkbox */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" required className="mt-1 shrink-0 accent-red-700" />
              <span className="text-xs text-gray-700 leading-relaxed">
                I confirm that this is a <strong>legitimate inquiry</strong> that meets the criteria above. I acknowledge that unsolicited sales, marketing, or promotional messages are strictly prohibited and may result in permanent blocking and legal action.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gray-900 text-white py-4 rounded-lg font-semibold text-sm hover:bg-gray-700 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
            ) : (
              <><i className="ri-send-plane-line"></i> Send Message</>
            )}
          </button>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-lg"></i>
              Your message has been received. We will respond if your inquiry qualifies.
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line text-lg"></i>
              There was an error. Please try again.
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const CareersForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    availability: '',
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d7ddhpckpdh0a4advvo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', phone: '', position: '', experience: '', availability: '', coverLetter: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
      {/* Left info column */}
      <div className="lg:col-span-2 space-y-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Join Our Team</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            We're always looking for dedicated, detail-oriented professionals to join the Villarreal Enterprise Group family. Submit your application below and our HR team will review it carefully.
          </p>
        </div>

        <div>
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Open Positions</h4>
          <div className="space-y-2">
            {[
              { title: 'DMV Document Specialist', type: 'Full-Time' },
              { title: 'Client Services Representative', type: 'Full-Time' },
              { title: 'VIN Inspector (Certified)', type: 'Full-Time / Part-Time' },
              { title: 'Office Administrator', type: 'Full-Time' },
              { title: 'General Application', type: 'Open' },
            ].map((pos) => (
              <div key={pos.title} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                <span className="text-sm font-medium text-gray-800">{pos.title}</span>
                <span className="text-xs text-gray-500 bg-gray-200 rounded-full px-2 py-0.5 whitespace-nowrap">{pos.type}</span>
              </div>
            ))}
          </div>
        </div>

        {/* HR Policy Warning */}
        <div className="border-2 border-orange-500 rounded-xl overflow-hidden">
          <div className="bg-orange-600 px-4 py-3 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-alarm-warning-line text-white text-base"></i>
            </div>
            <span className="text-white font-black text-xs tracking-widest uppercase">Important HR Policy</span>
          </div>
          <div className="bg-orange-50 px-4 py-4 space-y-3 text-xs text-gray-700">
            <p className="font-bold text-orange-900 text-sm leading-snug">
              Do NOT call or follow up on your application status.
            </p>
            <p className="leading-relaxed">
              Our HR department receives a high volume of applications. <strong>Calling to inquire about your application status, following up by phone, or making repeated contact will result in the immediate and permanent cancellation of your application</strong> — with no exceptions.
            </p>
            <div className="border-t border-orange-200 pt-3 space-y-2">
              <p className="font-bold text-gray-800">This includes:</p>
              <ul className="space-y-1.5">
                {[
                  'Calling to ask if your application was received',
                  'Calling to check your application status',
                  'Follow-up calls of any kind regarding employment',
                  'Repeated emails asking for status updates',
                  'Showing up in person to inquire about your application',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-close-circle-line text-orange-600"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 mt-2">
              <p className="font-black text-orange-800 text-center text-xs uppercase tracking-wide">
                HR will contact you directly if you are selected.
              </p>
              <p className="text-center text-orange-700 text-xs mt-1">
                If you have not been contacted, your application is still under review or was not selected at this time.
              </p>
            </div>
            <p className="text-center text-gray-600 text-xs italic border-t border-orange-200 pt-3">
              We appreciate your interest and respect your time. Please respect ours.
            </p>
          </div>
        </div>
      </div>

      {/* Right form column */}
      <div className="lg:col-span-3">
        {submitStatus === 'success' ? (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-bold text-green-800 mb-2">Application Submitted</h3>
            <p className="text-green-700 text-sm leading-relaxed max-w-sm mx-auto">
              Thank you for your interest in joining our team. Your application has been received and will be reviewed by our HR department.
            </p>
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-orange-800 font-bold text-sm">Reminder:</p>
              <p className="text-orange-700 text-xs mt-1">
                Please do <strong>not</strong> call or follow up. HR will contact you directly if you are selected. Follow-up calls will result in permanent application cancellation.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="careers-page-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                <input
                  type="text" name="fullName" required value={formData.fullName} onChange={handleChange}
                  placeholder="Your full legal name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address *</label>
                <input
                  type="email" name="email" required value={formData.email} onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number *</label>
                <input
                  type="tel" name="phone" required value={formData.phone} onChange={handleChange}
                  placeholder="(702) 000-0000"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Position Applying For *</label>
                <select
                  name="position" required value={formData.position} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
                >
                  <option value="">Select a position</option>
                  <option value="dmv-specialist">DMV Document Specialist</option>
                  <option value="client-services">Client Services Representative</option>
                  <option value="vin-inspector">VIN Inspector (Certified)</option>
                  <option value="office-admin">Office Administrator</option>
                  <option value="general">General Application</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Years of Experience *</label>
                <select
                  name="experience" required value={formData.experience} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
                >
                  <option value="">Select experience level</option>
                  <option value="entry">Entry Level (0–1 years)</option>
                  <option value="junior">Junior (1–3 years)</option>
                  <option value="mid">Mid-Level (3–5 years)</option>
                  <option value="senior">Senior (5+ years)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Availability *</label>
                <select
                  name="availability" required value={formData.availability} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50"
                >
                  <option value="">Select availability</option>
                  <option value="immediate">Immediate</option>
                  <option value="2-weeks">2 Weeks Notice</option>
                  <option value="1-month">1 Month</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cover Letter / Why You&apos;re a Good Fit *</label>
              <textarea
                name="coverLetter" required rows={6} maxLength={500}
                value={formData.coverLetter} onChange={handleChange}
                placeholder="Tell us about your relevant experience, skills, and why you want to join our team..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-gray-50 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.coverLetter.length}/500</p>
            </div>

            {/* HR Policy Acknowledgment */}
            <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 space-y-3">
              <p className="text-xs font-bold text-orange-800 uppercase tracking-wide">Before submitting, you must acknowledge:</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" required className="mt-1 shrink-0 accent-orange-600" />
                <span className="text-xs text-gray-700 leading-relaxed">
                  I understand that <strong>calling or following up on my application status will result in the immediate and permanent cancellation of my application.</strong> I agree to wait for HR to contact me directly if I am selected.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" required className="mt-1 shrink-0 accent-orange-600" />
                <span className="text-xs text-gray-700 leading-relaxed">
                  I confirm that all information provided in this application is accurate and truthful to the best of my knowledge.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-900 text-white py-4 rounded-lg font-semibold text-sm hover:bg-gray-700 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <><i className="ri-loader-4-line animate-spin"></i> Submitting Application...</>
              ) : (
                <><i className="ri-send-plane-line"></i> Submit Application</>
              )}
            </button>

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm flex items-center gap-2">
                <i className="ri-error-warning-line text-lg"></i>
                There was an error submitting your application. Please try again.
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default function ContactPage() {
  const [activeTab, setActiveTab] = useState<Tab>('contact');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'ContactPage',
        '@id': `${siteUrl}/contact#webpage`,
        url: `${siteUrl}/contact`,
        name: 'Contact & Careers | Villarreal Enterprise Group Las Vegas',
        description: 'Contact Villarreal Enterprise Group for legitimate DMV service inquiries. Also accepting career applications for Speedy DMV Services and DMV-Xpress Services in Las Vegas, Nevada.',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: { '@id': `${siteUrl}/contact#breadcrumb` },
        dateModified: '2026-04-11',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/contact#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Contact & Careers', item: `${siteUrl}/contact` },
        ],
      },
      {
        '@type': 'JobPosting',
        title: 'DMV Document Specialist',
        description: 'Join Villarreal Enterprise Group as a DMV Document Specialist. Handle vehicle registration, title transfers, and document preparation for clients in Las Vegas, Nevada.',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'Villarreal Enterprise Group',
          sameAs: siteUrl,
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Las Vegas',
            addressRegion: 'NV',
            postalCode: '89101',
            addressCountry: 'US',
          },
        },
        employmentType: 'FULL_TIME',
        datePosted: '2026-01-01',
        validThrough: '2026-12-31',
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: {
            '@type': 'QuantitativeValue',
            unitText: 'YEAR',
          },
        },
      },
      {
        '@type': 'JobPosting',
        title: 'Client Services Representative',
        description: 'Join Villarreal Enterprise Group as a Client Services Representative. Assist clients with DMV inquiries, process transactions, and deliver exceptional service in Las Vegas, Nevada.',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'Villarreal Enterprise Group',
          sameAs: siteUrl,
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Las Vegas',
            addressRegion: 'NV',
            postalCode: '89101',
            addressCountry: 'US',
          },
        },
        employmentType: 'FULL_TIME',
        datePosted: '2026-01-01',
        validThrough: '2026-12-31',
      },
      {
        '@type': 'JobPosting',
        title: 'VIN Inspector (Certified)',
        description: 'Villarreal Enterprise Group is hiring a Certified VIN Inspector for DMV-Xpress Services™ in Las Vegas, Nevada. Perform Nevada DMV-compliant VIN verifications for out-of-state vehicles and rebuilt titles.',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'Villarreal Enterprise Group',
          sameAs: siteUrl,
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Las Vegas',
            addressRegion: 'NV',
            postalCode: '89101',
            addressCountry: 'US',
          },
        },
        employmentType: ['FULL_TIME', 'PART_TIME'],
        datePosted: '2026-01-01',
        validThrough: '2026-12-31',
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Contact & Careers | Villarreal Enterprise Group Las Vegas"
        description="Contact Villarreal Enterprise Group for legitimate DMV service inquiries. Also accepting career applications for Speedy DMV Services and DMV-Xpress Services in Las Vegas, Nevada."
        keywords="contact Villarreal Enterprise Group, Las Vegas DMV careers, DMV jobs Nevada, Speedy DMV employment"
        canonical={`${siteUrl}/contact`}
        schema={schema}
        schemaId="schema-contact"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gray-900 py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5d475aa6a24f96834ac7b2b71d7c6219.jpg"
            alt="Contact us"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/90 to-gray-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Contact & Careers</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact &amp; Careers</h1>
          <p className="text-gray-300 max-w-xl leading-relaxed">
            Reach out for legitimate service inquiries, or apply to join our growing team in Las Vegas.
          </p>
        </div>
      </section>

      {/* Tab Switcher */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 py-3 w-fit">
            <button
              onClick={() => setActiveTab('contact')}
              className={`flex items-center gap-2 px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'contact' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line text-sm"></i>
              </div>
              Contact Us
            </button>
            <button
              onClick={() => setActiveTab('careers')}
              className={`flex items-center gap-2 px-6 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'careers' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-briefcase-line text-sm"></i>
              </div>
              Careers
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'contact' ? <ContactForm /> : <CareersForm />}
        </div>
      </section>

      <Footer />
    </>
  );
}
